import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Filter, X, SlidersHorizontal, BellPlus, FileSearch } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CITIES, ARRONDISSEMENTS, ALL_NEIGHBORHOODS, PROPERTY_TYPES, TRANSACTION_TYPES } from "../lib/constants";
import PropertyCard from "../components/PropertyCard";
import SearchRequestModal from "../components/SearchRequestModal";

const PAGE_SIZE = 12;

const QUICK_TABS = [
  { label: "Tous", value: "" },
  { label: "Vente", value: "vente" },
  { label: "Location mensuelle", value: "location_mensuelle" },
  { label: "Location journalière", value: "location_journaliere" },
  { label: "Location hebdo.", value: "location_hebdomadaire" },
  { label: "Location annuelle", value: "location_annuelle" },
];

const SearchPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [savingSearch, setSavingSearch] = useState(false);
  const [showRequest, setShowRequest] = useState(false);

  const filters = {
    q: params.get("q") || "",
    city: params.get("city") || "",
    arrondissement: params.get("arrondissement") || "",
    neighborhood: params.get("neighborhood") || "",
    property_type: params.get("property_type") || "",
    transaction_type: params.get("transaction_type") || "",
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
    min_area: params.get("min_area") || "",
    verified: params.get("verified") || "",
  };

  const fetchData = async (append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    const q = { limit: PAGE_SIZE, skip: append ? items.length : 0 };
    Object.entries(filters).forEach(([k, v]) => { if (v) q[k] = v; });
    try {
      const res = await api.get("/properties", { params: q });
      const list = res.data || [];
      const totalHdr = parseInt(res.headers?.["x-total-count"] || "0", 10) || list.length;
      setTotal(totalHdr);
      setItems(prev => append ? [...prev, ...list] : list);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => { fetchData(false); /* eslint-disable-next-line */ }, [params.toString()]);

  const update = (k, v) => {
    const np = new URLSearchParams(params);
    if (v) np.set(k, v); else np.delete(k);
    setParams(np);
  };
  const reset = () => setParams(new URLSearchParams());

  const saveSearch = async () => {
    if (!user) {
      toast.info("Connectez-vous pour créer une alerte de recherche");
      window.location.href = "/login";
      return;
    }
    const active = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (Object.keys(active).length === 0) {
      toast.error("Choisissez au moins un filtre (ville, type, prix…) avant de créer l'alerte");
      return;
    }
    setSavingSearch(true);
    try {
      await api.post("/saved-searches", { filters: active, email_alerts: true });
      toast.success("🔔 Alerte créée ! Vous recevrez un email dès qu'un bien correspondant sera publié.", { duration: 6000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la création de l'alerte");
    } finally {
      setSavingSearch(false);
    }
  };

  const neighborhoods = filters.arrondissement ? ARRONDISSEMENTS[filters.arrondissement] || [] : ALL_NEIGHBORHOODS;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Quick filter tabs */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar mb-5 pb-1" data-testid="search-quick-tabs">
        {QUICK_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => update("transaction_type", tab.value)}
            data-testid={`tab-${tab.value || "all"}`}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition ${
              filters.transaction_type === tab.value
                ? "bg-[#0A0A0A] text-white shadow-md"
                : "bg-white border border-neutral-200 text-neutral-600 hover:border-neutral-400"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter">{t("search.title")}</h1>
          <p className="text-sm text-neutral-500 mt-1">{items.length} {t("search.results")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowRequest(true)}
            data-testid="deposit-search-btn"
            className="hidden sm:flex items-center gap-2 h-10 px-4 rounded-lg text-sm font-bold bg-[#FF6B1A] hover:bg-[#E65A10] text-white transition"
          >
            <FileSearch className="h-4 w-4" /> Déposer une recherche
          </button>
          <button
            onClick={saveSearch}
            disabled={savingSearch}
            data-testid="save-search-btn"
            className="hidden sm:flex items-center gap-2 h-10 px-4 rounded-lg text-sm font-bold bg-[#00B4FF] hover:bg-[#009EE0] text-white transition disabled:opacity-60"
          >
            <BellPlus className="h-4 w-4" /> {savingSearch ? "Création…" : "Créer une alerte"}
          </button>
          <button onClick={() => setShowFilters(true)} data-testid="show-filters-btn" className="lg:hidden imora-btn-outline !h-10 !px-4">
            <SlidersHorizontal className="h-4 w-4" /> {t("search.filters")}
          </button>
        </div>
      </div>

      {/* Mobile save-search bar */}
      <div className="sm:hidden grid grid-cols-1 gap-2 mb-4">
        <button
          onClick={() => setShowRequest(true)}
          data-testid="deposit-search-btn-mobile"
          className="w-full flex items-center justify-center gap-2 h-11 rounded-lg text-sm font-bold bg-[#FF6B1A] text-white"
        >
          <FileSearch className="h-4 w-4" /> Déposer une recherche
        </button>
        <button
          onClick={saveSearch}
          disabled={savingSearch}
          data-testid="save-search-btn-mobile"
          className="w-full flex items-center justify-center gap-2 h-11 rounded-lg text-sm font-bold bg-[#00B4FF] text-white disabled:opacity-60"
        >
          <BellPlus className="h-4 w-4" /> {savingSearch ? "Création…" : "Créer une alerte email pour cette recherche"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Filters - desktop */}
        <aside className="hidden lg:block sticky top-24 self-start bg-white rounded-xl border border-neutral-200 p-4">
          <FilterPanel filters={filters} update={update} reset={reset} neighborhoods={neighborhoods} />
        </aside>

        {/* Mobile filter sheet */}
        {showFilters && (
          <div className="fixed inset-0 z-50 bg-black/50" onClick={() => setShowFilters(false)}>
            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold text-xl">{t("search.filters")}</h3>
                <button onClick={() => setShowFilters(false)} data-testid="close-filters-btn"><X className="h-5 w-5" /></button>
              </div>
              <FilterPanel filters={filters} update={update} reset={reset} neighborhoods={neighborhoods} />
              <button onClick={() => setShowFilters(false)} className="imora-btn-primary w-full mt-4">{t("search.apply")}</button>
            </div>
          </div>
        )}

        {/* Results */}
        <div>
          {loading ? (
            <div className="text-center py-20 text-neutral-500">{t("common.loading")}</div>
          ) : items.length === 0 ? (
            <div className="bg-white border border-neutral-200 rounded-xl p-12 text-center">
              <Filter className="h-10 w-10 mx-auto text-neutral-400 mb-3" />
              <p className="text-neutral-600 mb-4">{t("search.noResults")}</p>
              <button onClick={() => setShowRequest(true)} data-testid="deposit-search-empty-btn" className="imora-btn-primary inline-flex">
                <FileSearch className="h-4 w-4" /> Déposer une recherche — on vous contacte
              </button>
            </div>
          ) : (
            <>
              <div className="text-sm text-neutral-500 font-semibold mb-3" data-testid="search-count">
                {items.length} / {total} {t("search.results")}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                {items.map((p) => <PropertyCard key={p.id} property={p} />)}
              </div>
              {items.length < total && (
                <div className="text-center mt-6">
                  <button
                    onClick={() => fetchData(true)}
                    disabled={loadingMore}
                    data-testid="load-more-btn"
                    className="imora-btn-primary px-8"
                  >
                    {loadingMore ? t("common.loading_more") : `${t("common.loadMore")} (${total - items.length})`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {showRequest && <SearchRequestModal onClose={() => setShowRequest(false)} />}
    </div>
  );
};

const FilterPanel = ({ filters, update, reset, neighborhoods }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div>
        <label className="imora-label">{t("search.placeholder")}</label>
        <input data-testid="filter-q" value={filters.q} onChange={(e) => update("q", e.target.value)} className="imora-input" />
      </div>
      <div>
        <label className="imora-label">{t("search.city")}</label>
        <select data-testid="filter-city" value={filters.city} onChange={(e) => update("city", e.target.value)} className="imora-input">
          <option value="">{t("common.all")}</option>
          {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="imora-label">{t("search.arrondissement")}</label>
        <select data-testid="filter-arr" value={filters.arrondissement} onChange={(e) => { update("arrondissement", e.target.value); update("neighborhood", ""); }} className="imora-input">
          <option value="">{t("common.all")}</option>
          {Object.keys(ARRONDISSEMENTS).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      <div>
        <label className="imora-label">{t("search.neighborhood")}</label>
        <select data-testid="filter-neigh" value={filters.neighborhood} onChange={(e) => update("neighborhood", e.target.value)} className="imora-input">
          <option value="">{t("common.all")}</option>
          {neighborhoods.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <div>
        <label className="imora-label">{t("search.propertyType")}</label>
        <select data-testid="filter-type" value={filters.property_type} onChange={(e) => update("property_type", e.target.value)} className="imora-input">
          <option value="">{t("common.all")}</option>
          {Object.values(PROPERTY_TYPES).map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.items.map(it => <option key={it.value} value={it.value}>{it.label}</option>)}
            </optgroup>
          ))}
        </select>
      </div>
      <div>
        <label className="imora-label">{t("search.transactionType")}</label>
        <select data-testid="filter-transaction" value={filters.transaction_type} onChange={(e) => update("transaction_type", e.target.value)} className="imora-input">
          <option value="">{t("common.all")}</option>
          {TRANSACTION_TYPES.map(tr => <option key={tr.value} value={tr.value}>{tr.label}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="imora-label">{t("search.minPrice")}</label>
          <input data-testid="filter-min-price" type="number" value={filters.min_price} onChange={(e) => update("min_price", e.target.value)} className="imora-input" />
        </div>
        <div>
          <label className="imora-label">{t("search.maxPrice")}</label>
          <input data-testid="filter-max-price" type="number" value={filters.max_price} onChange={(e) => update("max_price", e.target.value)} className="imora-input" />
        </div>
      </div>
      <div>
        <label className="imora-label">{t("search.minArea")}</label>
        <input data-testid="filter-min-area" type="number" value={filters.min_area} onChange={(e) => update("min_area", e.target.value)} className="imora-input" />
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input data-testid="filter-verified" type="checkbox" checked={filters.verified === "true"} onChange={(e) => update("verified", e.target.checked ? "true" : "")} className="h-4 w-4 accent-[#00B4FF]" />
        <span className="text-sm font-semibold">{t("search.verifiedOnly")}</span>
      </label>
      <button onClick={reset} data-testid="filter-reset" className="imora-btn-outline w-full">{t("search.reset")}</button>
    </div>
  );
};

export default SearchPage;
